import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCreateMarketplaceMTGithubInstallationMutation } from 'network/mutations/useCreateMarketplaceMTGithubInstallationMutation';
import {
  MARKETPLACE_PHASES,
  MarketplaceErrorCode,
  MarketplacePhase,
} from '../consts';
import {
  buildUrlWithoutMarketplaceParams,
  clearMarketplaceInstallationState,
  deriveMarketplacePhaseFromMutation,
  getMarketplaceErrorCode,
  parseMarketplaceParamsFromSearch,
} from '../utils';

interface UseGithubMarketplaceFlowResult {
  isMarketplaceFlow: boolean;
  marketplacePhase: MarketplacePhase;
  marketplaceErrorCode: MarketplaceErrorCode | undefined;
}

/**
 * Orchestrates the GitHub Marketplace landing flow for the MT installation
 * wizard. Owns the marketplace mutation and all marketplace-only side effects
 * so the dialog component can focus on rendering.
 *
 * Lifecycle (only active when the URL is a marketplace landing):
 *  1. Fire the create-installation mutation exactly once with the URL payload.
 *     The persisted localStorage entry is owned by
 *     `GithubMarketplaceWizardProtectedRoute` / `getPostOnboardingDestination`
 *     for resume-after-signin and
 *     resume-after-onboarding — the wizard always sees URL params present.
 *  2. Derive `marketplacePhase` + `marketplaceErrorCode` from the mutation
 *     snapshot via the pure `deriveMarketplacePhaseFromMutation` helper.
 *  3. On a terminal non-error phase (success / already-installed): clear the
 *     persisted entry and strip marketplace query params from the URL.
 *  4. On any HTTP error: clear the persisted entry and strip marketplace
 *     query params. 401s are handled globally by the auth interceptor
 *     (token refresh + signout-on-fail).
 */
export const useGithubMarketplaceFlow = (): UseGithubMarketplaceFlowResult => {
  const { mutate, status, error } =
    useCreateMarketplaceMTGithubInstallationMutation();
  const navigate = useNavigate();
  const location = useLocation();

  const marketplaceLanding = parseMarketplaceParamsFromSearch(location.search);
  const isMarketplaceFlow = marketplaceLanding.isMarketplaceLanding;

  const [marketplacePhase, setMarketplacePhase] = useState<MarketplacePhase>(
    MARKETPLACE_PHASES.IDLE,
  );
  const [marketplaceErrorCode, setMarketplaceErrorCode] =
    useState<MarketplaceErrorCode>();

  // This effect is for firing the create-installation mutation exactly once on a marketplace landing using the URL payload.
  useEffect(() => {
    const didMutationFire = status !== 'idle';
    const shouldFireMutation = isMarketplaceFlow && !didMutationFire;

    if (!shouldFireMutation) {
      return;
    }

    mutate({
      installationId: marketplaceLanding.installationId as number,
      state: marketplaceLanding.state as string,
    });
  }, [
    isMarketplaceFlow,
    marketplaceLanding.installationId,
    marketplaceLanding.state,
    mutate,
    status,
  ]);

  // This effect is for deriving the marketplace phase and error code from the mutation status and pushing them into local state.
  useEffect(() => {
    if (!isMarketplaceFlow) {
      return;
    }

    const { phase, errorCode: derivedErrorCode } =
      deriveMarketplacePhaseFromMutation({
        isMarketplaceFlow,
        status,
        error,
      });

    setMarketplacePhase(phase);
    setMarketplaceErrorCode(derivedErrorCode);
  }, [isMarketplaceFlow, status, error]);

  // This effect is for cleaning up persisted state and stripping marketplace query params once the flow reaches a terminal non-error phase.
  useEffect(() => {
    if (!isMarketplaceFlow) {
      return;
    }

    const isTerminalNonError =
      marketplacePhase === MARKETPLACE_PHASES.SUCCESS ||
      marketplacePhase === MARKETPLACE_PHASES.ALREADY_INSTALLED;

    if (isTerminalNonError) {
      clearMarketplaceInstallationState();
      navigate(
        buildUrlWithoutMarketplaceParams(location.pathname, location.search),
        { replace: true },
      );
    }
  }, [isMarketplaceFlow, marketplacePhase]);

  // This effect is for clearing persisted state and stripping marketplace query params on any HTTP error from the mutation.
  useEffect(() => {
    if (!isMarketplaceFlow || status !== 'error') {
      return;
    }

    const { statusCode } = getMarketplaceErrorCode(error);
    const isHttpResponseError = statusCode !== undefined;

    if (isHttpResponseError) {
      clearMarketplaceInstallationState();
      navigate(
        buildUrlWithoutMarketplaceParams(location.pathname, location.search),
        { replace: true },
      );
    }
  }, [isMarketplaceFlow, status, error]);

  return {
    isMarketplaceFlow,
    marketplacePhase,
    marketplaceErrorCode,
  };
};
